import React, { useState, useRef, useCallback, useEffect } from "react";

import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import Headerbar from "../common/Headerbar";
import Breadcrumbs from "../common/Breadcrumbs";
import { EpisodesIcon } from "../common/icons";
import { episodeService } from "../Episode/services";
import { audiogramsService } from "../Audiograms/services";
import {
  audiogramToEditorState,
  getDefaultEditorState,
} from "../Audiograms/components/templates/AudiogramRenderer";
import { AudiogramEditorState, AspectRatio } from "../Audiograms/types";
import EditorPanel from "./components/EditorPanel";
import LivePreview from "./components/LivePreview";
import ExportModal from "./components/ExportModal";
import { EDITOR_TOKENS } from "./editorTokens";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useExportDownload } from "../../hooks/useExportDownload";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";

type SaveStatus = "idle" | "saving" | "saved" | "error";

function getFileExtension(url: string, fallback = "mp4"): string {
  const clean = url.split("?")[0].split("#")[0];
  const ext = clean.split(".").pop();
  if (ext && ext.length <= 4 && /^[a-z0-9]+$/i.test(ext)) return ext;
  return fallback;
}

export default function AudiogramEditor() {
  const { id: episodeId, audiogramId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sidebarRef = useRef<SidebarRef>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Latest transcript paragraph edit to persist via the backend `paragraph`
  // field (kept in a ref so the debounced autosave always reads the newest one
  // without re-triggering on every keystroke).
  const pendingParagraphEditRef = useRef<{ id: string; text: string } | null>(
    null,
  );

  const {
    isExporting,
    setIsExporting,
    exportError,
    setExportError,
    isExportingRef,
    downloadFile,
  } = useExportDownload();

  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
    enabled: !!episodeId,
  });

  const {
    data: audiogram,
    isLoading: audiogramLoading,
    isError: audiogramError,
  } = useQuery({
    queryKey: ["getAudiogram", audiogramId],
    queryFn: () => audiogramsService.getAudiogramById(audiogramId),
    enabled: !!audiogramId,
  });

  const [editorState, setEditorState] = useState<AudiogramEditorState>(
    getDefaultEditorState("dark-minimal"),
  );

  const [initialized, setInitialized] = useState(false);
  const socket = useWebSocket();

  // Listen for the audiogram export completion event and download the result.
  useEffect(() => {
    if (!socket) return undefined;

    const handleExportDone = async (data: {
      exported_video_url?: string;
      audiogram?: { id: string; title: string };
    }) => {
      setIsExporting(false);
      if (data?.exported_video_url) {
        try {
          const title = data?.audiogram?.title ?? "audiogram";
          const ext = getFileExtension(data.exported_video_url);
          await downloadFile(data.exported_video_url, `${title}.${ext}`);
          setExportSuccess(true);
        } catch {
          setExportError(
            "Your audiogram exported, but the download failed to start. Please try again.",
          );
        }
      }
    };

    // Only surface a backend error if an export is in flight, otherwise the
    // export backdrop could stay stuck forever.
    const handleSocketError = () => {
      if (isExportingRef.current) {
        setIsExporting(false);
        setExportError("Export failed. Please try again.");
      }
    };

    socket.on(WebSocketEventName.AUDIOGRAM_EXPORT_DONE, handleExportDone);
    socket.on(WebSocketEventName.ERROR, handleSocketError);

    return () => {
      socket.off(WebSocketEventName.AUDIOGRAM_EXPORT_DONE, handleExportDone);
      socket.off(WebSocketEventName.ERROR, handleSocketError);
    };
  }, [socket, downloadFile, setIsExporting, setExportError, isExportingRef]);

  useEffect(() => {
    if (audiogram && !initialized) {
      const next = audiogramToEditorState(audiogram);
      // Always open the editor at the start (first transcript segment) rather
      // than resuming the last edited/selected caption.
      if (next.transcriptParagraphs.length > 0) {
        next.selectedParagraphIndex = 0;
        next.quote = next.transcriptParagraphs[0].text;
      }
      setEditorState(next);
      setInitialized(true);
      // We default `animated` to true when the stored value is missing; persist
      // it so playback and export reflect the visible (animated) waveform.
      if (audiogram.animated == null) {
        setIsDirty(true);
      }
    }
  }, [audiogram, initialized]);

  useEffect(() => {
    if (!isDirty || !audiogramId) return undefined;

    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setSaveStatus("saving");

    const timer = setTimeout(async () => {
      try {
        await audiogramsService.updateAudiogram(
          audiogramId,
          editorState,
          pendingParagraphEditRef.current ?? undefined,
        );
        pendingParagraphEditRef.current = null;
        queryClient.invalidateQueries({
          queryKey: ["getAudiograms", episodeId],
        });
        setSaveStatus("saved");
        savedTimerRef.current = setTimeout(() => {
          setSaveStatus("idle");
        }, 2000);
      } catch {
        setSaveStatus("error");
        savedTimerRef.current = setTimeout(() => {
          setSaveStatus("idle");
        }, 3000);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [editorState, isDirty, audiogramId]);

  const episodeName = episode?.name || "Episode";
  const audiogramTitle = audiogram?.title || "Audiogram";

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleChange = useCallback((update: Partial<AudiogramEditorState>) => {
    setEditorState((prev) => ({ ...prev, ...update }));
    setIsDirty(true);
  }, []);

  const [currentTime, setCurrentTime] = useState(0);
  const audioUrl = episode?.upload?.url;

  const handleAspectRatioChange = useCallback((ratio: AspectRatio) => {
    setEditorState((prev) => ({ ...prev, aspectRatio: ratio }));
    setIsDirty(true);
  }, []);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const hasError = !audiogramId || audiogramError;
  const isLoading = !hasError && (audiogramLoading || !initialized);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView
        pageName="Edit Audiogram"
        expended={false}
        hidePrimary
        ref={sidebarRef}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Headerbar
          pageName={audiogramTitle}
          onMobileMenuClick={handleMobileMenuToggle}
          headerLeftContent={
            <Breadcrumbs
              items={[
                {
                  label: "Episodes",
                  path: "/episodes",
                  icon: <EpisodesIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: episodeName,
                  path: `/episodes/${episodeId}`,
                },
                {
                  label: "Audiograms",
                  path: `/episodes/${episodeId}/audiograms`,
                  icon: <GraphicEqIcon sx={{ fontSize: 18 }} />,
                },
                {
                  label: audiogramTitle,
                  path: `/episodes/${episodeId}/audiograms/${audiogramId}`,
                  icon: <EditIcon sx={{ fontSize: 18 }} />,
                },
              ]}
              showHome={false}
              inHeader
            />
          }
        />

        {/* Action bar */}
        {!hasError && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: EDITOR_TOKENS.panelSurface,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {saveStatus !== "idle" && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    borderRadius: "999px",
                    backgroundColor: (() => {
                      if (saveStatus === "saved")
                        return "rgba(16,185,129,0.10)";
                      if (saveStatus === "error") return "rgba(239,68,68,0.10)";
                      return "rgba(15,23,42,0.06)";
                    })(),
                  }}
                >
                  {saveStatus === "saving" && (
                    <>
                      <CloudSyncIcon
                        sx={{
                          fontSize: 15,
                          color: "text.secondary",
                          animation: "spin 1s linear infinite",
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      >
                        Saving…
                      </Typography>
                    </>
                  )}
                  {saveStatus === "saved" && (
                    <>
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 15, color: EDITOR_TOKENS.success }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: EDITOR_TOKENS.success, fontWeight: 600 }}
                      >
                        Saved
                      </Typography>
                    </>
                  )}
                  {saveStatus === "error" && (
                    <Typography
                      variant="caption"
                      sx={{ color: "error.main", fontWeight: 600 }}
                    >
                      Save failed
                    </Typography>
                  )}
                </Box>
              )}

              <Button
                variant="contained"
                startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                onClick={() => setExportOpen(true)}
                disabled={isLoading || isExporting}
                sx={{
                  backgroundColor: EDITOR_TOKENS.accent,
                  borderRadius: EDITOR_TOKENS.controlRadius,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "14px",
                  px: 2.5,
                  py: 0.9,
                  boxShadow: "0 4px 12px rgba(37,99,235,0.32)",
                  "&:hover": {
                    backgroundColor: EDITOR_TOKENS.accentDark,
                    boxShadow: "0 6px 18px rgba(37,99,235,0.42)",
                  },
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        )}

        {/* Main content: left panel + center preview. Stacks vertically on
            small screens so the preview isn't squeezed next to the panel. */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflow: { xs: "auto", md: "hidden" },
            backgroundColor: (theme) => theme.palette.grey[50],
          }}
        >
          {hasError && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                p: 4,
                textAlign: "center",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 56, color: "text.disabled" }} />
              <Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                  Couldn&apos;t load this audiogram
                </Typography>
                <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                  It may have been removed, or something went wrong. Please try
                  again.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate(`/episodes/${episodeId}/audiograms`)}
                sx={{
                  backgroundColor: EDITOR_TOKENS.accent,
                  borderRadius: EDITOR_TOKENS.controlRadius,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: EDITOR_TOKENS.accentDark },
                }}
              >
                Back to audiograms
              </Button>
            </Box>
          )}

          {isLoading && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: EDITOR_TOKENS.accent }} />
              <Typography sx={{ color: "text.secondary", fontWeight: 500 }}>
                Loading audiogram…
              </Typography>
            </Box>
          )}

          {!hasError && !isLoading && (
            <>
              {/* Left panel */}
              <Box
                sx={{
                  width: { xs: "100%", md: 384 },
                  minWidth: { md: 384 },
                  maxWidth: { md: 384 },
                  flexShrink: 0,
                  maxHeight: { xs: "45vh", md: "none" },
                  borderRight: { md: "1px solid" },
                  borderBottom: { xs: "1px solid", md: "none" },
                  borderColor: "divider",
                  backgroundColor: EDITOR_TOKENS.panelSurface,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <EditorPanel
                  state={editorState}
                  onChange={handleChange}
                  onParagraphEdit={(id, text) => {
                    pendingParagraphEditRef.current = { id, text };
                  }}
                  currentTime={currentTime}
                  audiogramId={audiogramId}
                />
              </Box>

              {/* Center preview */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: { xs: 480, md: 0 },
                  overflow: "hidden",
                  display: "flex",
                  backgroundColor: (theme) => theme.palette.grey[100],
                  backgroundImage: `radial-gradient(circle at 1px 1px, ${EDITOR_TOKENS.canvasDotColor} 1px, transparent 0)`,
                  backgroundSize: EDITOR_TOKENS.canvasDotSize,
                }}
              >
                <LivePreview
                  state={editorState}
                  audioUrl={audioUrl}
                  onAspectRatioChange={handleAspectRatioChange}
                  onTimeUpdate={handleTimeUpdate}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={async (params) => {
          if (!audiogramId) return;
          setExportError(null);
          setIsExporting(true);
          try {
            await audiogramsService.exportAudiogram(audiogramId, {
              video_resolution: params.resolution,
              compression_level: params.compressionLevel,
              fps: params.fps,
              include_subtitle: params.includeSubtitles,
              format: params.format,
            });
          } catch {
            setIsExporting(false);
            setExportError("We couldn't start the export. Please try again.");
          }
        }}
      />

      {/* Export progress */}
      <Backdrop
        open={isExporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: "#fff",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>
          Exporting your audiogram…
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          This can take a little while. The download starts automatically.
        </Typography>
      </Backdrop>

      {/* Export feedback */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setExportError(null)}
          sx={{ width: "100%" }}
        >
          {exportError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={exportSuccess}
        autoHideDuration={4000}
        onClose={() => setExportSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setExportSuccess(false)}
          sx={{ width: "100%" }}
        >
          Audiogram exported — your download is starting.
        </Alert>
      </Snackbar>
    </Box>
  );
}
